import { NgZone } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { Class, ScreenDefinition, ScreenDialogOptions } from '@lg/shared'
import { original, isDraft } from 'immer'
import { AppContext } from '../app-context.js'
import { ProxyUtil } from '@lg/shared'
import { FormCanDeactivate } from '../directives/form-can-deactivate.js'

export interface ScreenDialogData {
	data: any
	closeOnOutput: string
}

export interface ScreenNavigationOptions {
	askIfDirtyForm?: boolean
}

export class FrontendScreenBridge {
	static navigateToScreen<P extends object>(
		screen: ScreenDefinition<P, {}>,
		params: P,
		options?: ScreenNavigationOptions,
	) {
		const screenName =
			AppContext.appModuleId == screen.moduleId
				? screen.boId
				: `${screen.moduleId}.${screen.boId}`
		const router = AppContext.resolve(Router)

		if (!options?.askIfDirtyForm) {
			FormCanDeactivate.unregisterAllForms()
		}

		router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			// first fake navigation to "/"" to force Angular router to refresh
			router.navigate([screenName], {
				queryParams: params,
			})
		})
	}

	static navigateBack(steps?: number) {
		steps = steps ?? 1
		for (let i = 0; i < steps; i++) {
			window.history.back()
		}
	}

	static navigateForward(steps?: number) {
		steps = steps ?? 1
		for (let i = 0; i < steps; i++) {
			window.history.forward()
		}
	}

	static openUrl(url: string, inNewWindow: boolean | string) {
		if (inNewWindow) {
			window.open(url, typeof inNewWindow == 'string' ? inNewWindow : undefined)
		} else {
			window.location.assign(url)
		}
	}

	static async openDialogAndGetResponse<
		R,
		P extends object = {},
		I extends object = {},
		O extends Record<string, any> = Record<string, any>,
	>(screen: ScreenDefinition<P, I, O>, data: P & I, closeOnOutput: string, options?: ScreenDialogOptions): Promise<R> {
		const ngZone = AppContext.resolve(NgZone)
		const dialog = AppContext.resolve(MatDialog)

		if (typeof data == 'object') {
			const dataAny = data as any
			for (const key in dataAny) {
				if (isDraft(dataAny[key])) dataAny[key] = original(dataAny[key])
			}
		}

		const dialogData: ScreenDialogData = { data, closeOnOutput }

		return new Promise<R>((resolve) => {
			ngZone.run(() => {
				const dialogRef = dialog.open(screen.component, {
					...options,
					data: dialogData,
				})
				dialogRef.afterClosed().subscribe((result) => resolve(result))
			})
		})
	}

	static closeDialogAndReturn(result: any) {
		result = ProxyUtil.getTarget(result)

		const dialog = AppContext.resolve(MatDialog)
		const dialogRef = dialog.openDialogs.at(-1)

		if (!dialogRef) {
			console.error('DialogRef not found when trying to close dialog')
		}

		dialogRef?.close(result)
	}

	static cancelDialog() {
		this.closeDialogAndReturn(undefined)
	}
}
